import Analytics from '@/utils/analytics';
import CheckmarkIcon from '@assets/icons/checkmark.svg';
import i18n from '@/utils/i18n';
import LockIcon from '@assets/icons/lock.svg';
import PencilIcon from '@assets/icons/pencil-thin.svg';
import PlusIcon from '@assets/icons/plus.svg';
import React, { useRef, useState } from 'react';
import TrashIcon from '@assets/icons/trash-small-thin.svg';
import { Cart, useCartActions } from './hooks/use-cart-actions';
import { CombinedActionSheet } from '@/components/combined-action-sheet';
import { createStyles } from '@/contexts/theme-context';
import { DEFAULT_PADDING } from '@/components/base-action-sheet';
import { DeleteCartConfirmation } from './delete-cart-confirmation';
import {
  FlatList,
  Pressable,
  Text,
  useWindowDimensions,
  View,
} from 'react-native';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { SearchInput } from '@/components/search-input';
import { useLayoutContext } from '@/contexts/layout-context';

interface AllCartsSheetProps {
  onAddCart: () => void;
  onClose: () => void;
  onEdit: (cart: Cart) => void;
}

enum AllCartsRoute {
  Base = 'BASE',
  DeleteConfirmation = 'DELETE_CONFIRMATION',
}

export const AllCartsSheet: React.FC<AllCartsSheetProps> = ({
  onClose,
  onEdit,
  onAddCart,
}) => {
  const [styles, theme] = useStyles();
  const { activeCart, atCap, carts, selectCart, showCartLimitToast } =
    useCartActions();
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingCart, setDeletingCart] = useState<Cart | null>(null);
  const [listHeight, setListHeight] = useState(0);
  const [listContentHeight, setListContentHeight] = useState(0);
  const { isSmallLayout } = useLayoutContext();
  const pendingHandoffRef = useRef<(() => void) | null>(null);

  const windowDimensions = useWindowDimensions();
  const actionSheetMaxHeight = Math.floor(windowDimensions.height * 0.6);
  const isListScrollable = listContentHeight > listHeight;

  const shownCarts = searchTerm
    ? carts.filter((cart) =>
        cart.name.toLowerCase().includes(searchTerm.trim().toLowerCase()),
      )
    : carts;

  return (
    <CombinedActionSheet
      contentStyle={{ maxHeight: actionSheetMaxHeight }}
      hasStickyHeader={false}
      initialRoute={AllCartsRoute.Base}
      modalHeaderStyle={styles.collapsedHeader}
      onClose={() => {
        const handoff = pendingHandoffRef.current;
        pendingHandoffRef.current = null;
        onClose();
        handoff?.();
      }}
      renderScene={({ close, jumpTo, routeKey }) => {
        if (routeKey === AllCartsRoute.DeleteConfirmation) {
          return (
            <View style={styles.confirmationContent}>
              <DeleteCartConfirmation
                onCancel={() => jumpTo(AllCartsRoute.Base)}
                onDeleted={() => jumpTo(AllCartsRoute.Base)}
                orderUUID={deletingCart?.orderUUID ?? null}
              />
            </View>
          );
        }

        return (
          <View
            style={[
              styles.content,
              isSmallLayout ? null : styles.contentLargeLayout,
            ]}
          >
            <View style={styles.header}>
              <Text style={TYPOGRAPHY.h2}>
                {i18n.t('multi_cart.all_carts')}
              </Text>
              <Text style={[TYPOGRAPHY.h4, styles.count]}>
                {i18n.t('multi_cart.open_count', { count: carts.length })}
              </Text>
            </View>
            <SearchInput
              autoCorrect={false}
              containerStyle={styles.search}
              onChangeText={setSearchTerm}
              placeholder={i18n.t('multi_cart.find_a_cart')}
              value={searchTerm}
            />
            <FlatList
              data={shownCarts}
              ItemSeparatorComponent={() => <View style={styles.rowDivider} />}
              keyExtractor={(cart) => cart.id}
              onContentSizeChange={(_width, height) =>
                setListContentHeight(height)
              }
              onLayout={(event) =>
                setListHeight(event.nativeEvent.layout.height)
              }
              renderItem={({ item: cart }) => (
                <View style={styles.row}>
                  <Pressable
                    onPress={async () => {
                      if (await selectCart(cart)) {
                        close();
                      }
                    }}
                    style={({ pressed }) => [
                      styles.rowMain,
                      pressed ? styles.pressed : null,
                    ]}
                    testID={`cart-row-${cart.id}`}
                  >
                    <View style={styles.checkColumn}>
                      {cart.id === activeCart.id ? (
                        <CheckmarkIcon
                          height={20}
                          stroke={theme.colors.accent}
                          width={20}
                        />
                      ) : null}
                    </View>
                    <View style={styles.nameRow}>
                      <Text
                        numberOfLines={1}
                        style={[TYPOGRAPHY.listTitle, styles.rowName]}
                      >
                        {cart.name}
                      </Text>
                      {cart.visibility === 'PRIVATE' ? (
                        <LockIcon
                          height={20}
                          stroke={theme.sk.color.icon.tertiary}
                          width={16}
                        />
                      ) : null}
                    </View>
                  </Pressable>
                  {cart.isDefault ? null : (
                    <Pressable
                      onPress={() => {
                        Analytics.track('multi_cart.cart_form.open', {
                          isEdit: true,
                        });
                        pendingHandoffRef.current = () => onEdit(cart);
                        close();
                      }}
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed ? styles.pressed : null,
                      ]}
                      testID={`edit-cart-${cart.id}`}
                    >
                      <PencilIcon
                        height={24}
                        stroke={theme.sk.color.icon.primary}
                        width={24}
                      />
                    </Pressable>
                  )}
                  {cart.isDefault ? null : (
                    <Pressable
                      onPress={() => {
                        Analytics.track('multi_cart.delete_cart.open');
                        setDeletingCart(cart);
                        jumpTo(AllCartsRoute.DeleteConfirmation);
                      }}
                      style={({ pressed }) => [
                        styles.iconButton,
                        pressed ? styles.pressed : null,
                      ]}
                      testID={`delete-cart-${cart.id}`}
                    >
                      <TrashIcon
                        height={24}
                        stroke={theme.sk.color.icon.negative.primary}
                        width={24}
                      />
                    </Pressable>
                  )}
                </View>
              )}
              style={styles.list}
            />
            <View
              style={[
                styles.footer,
                isListScrollable ? styles.footerDivider : null,
              ]}
            >
              <Pressable
                onPress={() => {
                  if (atCap) {
                    showCartLimitToast();
                    return;
                  }
                  Analytics.track('multi_cart.cart_form.open', {
                    isEdit: false,
                  });
                  pendingHandoffRef.current = onAddCart;
                  close();
                }}
                style={({ pressed }) => [
                  styles.newCart,
                  atCap ? styles.newCartDisabled : null,
                  pressed ? styles.pressed : null,
                ]}
              >
                <PlusIcon
                  height={20}
                  stroke={theme.sk.color.text.primary}
                  width={20}
                />
                <Text style={[TYPOGRAPHY.listTitle, styles.newCartLabel]}>
                  {i18n.t('multi_cart.add_cart')}
                </Text>
              </Pressable>
            </View>
          </View>
        );
      }}
      routes={{
        [AllCartsRoute.Base]: {
          previousKey: null,
          showCloseButton: false,
        },
        [AllCartsRoute.DeleteConfirmation]: {
          previousKey: null,
          showCloseButton: false,
        },
      }}
    />
  );
};

const useStyles = createStyles((theme) => ({
  checkColumn: { alignItems: 'center', width: 20 },
  collapsedHeader: { padding: 0 },
  confirmationContent: { paddingTop: 22 },
  content: { flexShrink: 1 },
  contentLargeLayout: { paddingTop: 22 },
  count: { color: theme.colors.inactiveText },
  footer: {
    paddingTop: 14,
  },
  footerDivider: {
    borderTopColor: theme.sk.color.divider.default,
    borderTopWidth: 1,
  },
  header: {
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  iconButton: {
    alignItems: 'center',
    borderRadius: 7,
    height: 28,
    justifyContent: 'center',
    width: 28,
  },
  list: { flexShrink: 1, marginHorizontal: -DEFAULT_PADDING },
  nameRow: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: 5,
    minWidth: 0,
  },
  newCart: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
    height: 48,
    justifyContent: 'center',
  },
  newCartDisabled: { opacity: 0.4 },
  newCartLabel: { ...FONTS.primaryBold },
  pressed: { opacity: 0.5 },
  row: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 4,
    paddingRight: DEFAULT_PADDING,
    paddingVertical: 16,
  },
  rowDivider: {
    backgroundColor: theme.sk.color.divider.default,
    height: 1,
    marginHorizontal: DEFAULT_PADDING,
  },
  rowMain: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    gap: 11,
    paddingLeft: DEFAULT_PADDING,
  },
  rowName: {
    ...FONTS.primaryBold,
    flexShrink: 1,
  },
  search: { backgroundColor: theme.colors.background, marginTop: 16 },
}));
